import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Newspaper, FileText, AlertTriangle, Users, Handshake, CreditCard, ArrowRight, Plus, Trash2, Euro, Receipt,
} from "lucide-react";
import { eur } from "@/lib/workflow";
import { STAGES, stageOf } from "@/components/PartnerServices";

function Implementation({ onOpen }) {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    api.get("/leistungen/plan").then((r) => {
      const c = Object.fromEntries(STAGES.map(([k]) => [k, 0]));
      r.data.types.forEach((t) => t.spaces.forEach((s) => s.bookings.forEach((b) => { c[stageOf(b.state)] += 1; })));
      setCounts(c);
    }).catch(() => {});
  }, []);
  if (!counts || !Object.values(counts).some(Boolean)) return null;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <div onClick={onOpen} className="mt-4 cursor-pointer bg-white border border-slate-200 border-l-4 border-l-green-700 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="dashboard-implementation">
      <div className="flex flex-wrap items-center gap-6">
        <div className="h-10 w-10 rounded-lg grid place-items-center bg-green-50 text-green-700"><Handshake size={20} /></div>
        <div className="min-w-[180px]">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Umsetzung Werbeflächen</div>
          <div className="text-sm text-slate-600"><b className="text-green-700">{counts.umgesetzt}</b> von {total} umgesetzt</div>
        </div>
        {STAGES.map(([k, l, cls]) => (
          <div key={k} data-testid={`dashboard-stage-${k}`}>
            <div className="text-2xl font-bold text-slate-900">{counts[k]}</div>
            <div className="text-xs text-slate-500 inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${cls}`} />{l}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden flex">
        {STAGES.map(([k, , cls]) => counts[k] > 0 && <div key={k} className={cls} style={{ width: `${(counts[k] / total) * 100}%` }} />)}
      </div>
    </div>
  );
}

function Receivables({ r, onOpen }) {
  if (!r) return null;
  return (
    <div onClick={onOpen} className="mt-4 cursor-pointer bg-white border border-slate-200 border-l-4 border-l-amber-500 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-wrap items-center gap-6" data-testid="dashboard-receivables">
      <div className="h-10 w-10 rounded-lg grid place-items-center bg-amber-50 text-amber-600"><Receipt size={20} /></div>
      <div className="min-w-[180px]">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Offene Posten</div>
        <div className="text-sm text-slate-600" data-testid="dashboard-receivables-count">{r.open_count} offene Rechnungen</div>
      </div>
      <div><div className="text-2xl font-bold text-slate-900" data-testid="dashboard-receivables-sum">{eur(r.open_sum)}</div><div className="text-xs text-slate-500">offen, brutto</div></div>
      <div><div className="text-2xl font-bold text-red-600" data-testid="dashboard-receivables-overdue">{r.overdue_count}</div><div className="text-xs text-slate-500">bereits fällig</div></div>
      {r.dunning_due_count > 0 && <div className="ml-auto text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1" data-testid="dashboard-receivables-dunning">{r.dunning_due_count} × Mahnung fällig</div>}
    </div>
  );
}

function AdRevenue({ r, onOpen }) {
  if (!r) return null;
  return (
    <div onClick={onOpen} className="mt-4 cursor-pointer bg-white border border-slate-200 border-l-4 border-l-emerald-500 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-wrap items-center gap-6" data-testid="dashboard-ad-revenue">
      <div className="h-10 w-10 rounded-lg grid place-items-center bg-emerald-50 text-emerald-600"><Euro size={20} /></div>
      <div className="min-w-[180px]">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Erwartete Anzeigen-Einnahmen</div>
        <div className="text-sm text-slate-600" data-testid="dashboard-ad-revenue-issue">{r.issue_name} · {r.count} Anzeigen</div>
      </div>
      <div><div className="text-2xl font-bold text-slate-900" data-testid="dashboard-ad-revenue-net">{eur(r.net)}</div><div className="text-xs text-slate-500">netto</div></div>
      <div><div className="text-2xl font-bold text-slate-900" data-testid="dashboard-ad-revenue-gross">{eur(r.gross)}</div><div className="text-xs text-slate-500">brutto inkl. {r.vat_rate} % MwSt.</div></div>
      <div className="text-xs text-slate-500 ml-auto">davon zugesagt <b className="text-emerald-700">{eur(r.confirmed)}</b> · in Prüfung <b className="text-amber-700">{eur(r.pending)}</b> (netto)</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg grid place-items-center ${tone}`}><Icon size={20} /></div>
      <div className="mt-4 text-3xl font-bold font-display text-slate-900">{value}</div>
      <div className="text-sm text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", redaktionsschluss: "", category_template_id: "" });
  const [catTemplates, setCatTemplates] = useState([]);
  useEffect(() => { api.get("/category-templates").then((r) => setCatTemplates(r.data)).catch(() => {}); }, []);

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createIssue = async () => {
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    try {
      const { data: issue } = await api.post("/issues", form);
      toast.success("Ausgabe angelegt");
      setOpen(false);
      setForm({ name: "", redaktionsschluss: "", category_template_id: "" });
      navigate(`/ausgabe/${issue.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeIssue = async (e, i) => {
    e.stopPropagation();
    if (!window.confirm(`Ausgabe "${i.name}" mit allen Artikeln löschen?`)) return;
    await api.delete(`/issues/${i.id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            Willkommen zurück, {user?.name?.split(" ")[0] || "Team"}
          </h1>
          <p className="text-slate-500 mt-1">Überblick über Ausgaben, Artikel und Anzeigenkunden.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-issue-button">
          <Plus size={16} className="mr-1.5" /> Neue Ausgabe
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat icon={Newspaper} label="Ausgaben" value={data?.issues ?? "–"} tone="bg-blue-50 text-blue-600" testid="stat-issues" />
        <Stat icon={FileText} label="Artikel gesamt" value={data?.articles ?? "–"} tone="bg-indigo-50 text-indigo-600" testid="stat-articles" />
        <Stat icon={AlertTriangle} label="Ohne Text" value={data?.open_text ?? "–"} tone="bg-red-50 text-red-600" testid="stat-open-text" />
        <Stat icon={Users} label="Kunden" value={data?.customers ?? "–"} tone="bg-slate-100 text-slate-600" testid="stat-customers" />
        <Stat icon={Handshake} label="Partner" value={data?.partners ?? "–"} tone="bg-emerald-50 text-emerald-600" testid="stat-partners" />
        <Stat icon={CreditCard} label="Anzeigenkunden" value={data?.paying ?? "–"} tone="bg-blue-50 text-blue-600" testid="stat-paying" />
      </div>
      <AdRevenue r={data?.ad_revenue} onOpen={() => navigate("/anzeigenkunden/anfragen")} />
      <Receivables r={data?.receivables} onOpen={() => navigate("/buchhaltung")} />
      <Implementation onOpen={() => navigate("/partner/belegungsplan")} />

      <div className="mt-8">
        <h2 className="font-display text-lg font-semibold text-slate-800 mb-3">Aktuelle Ausgaben</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data?.recent_issues || []).map((i) => (
            <div key={i.id} onClick={() => navigate(`/ausgabe/${i.id}`)}
              className="cursor-pointer bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-blue-400 hover:shadow-md transition group"
              data-testid={`dashboard-issue-${i.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-slate-900 text-lg">{i.name}</span>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => removeIssue(e, i)} className="text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>
                  <ArrowRight size={18} className="text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition" />
                </div>
              </div>
              <div className="text-sm text-slate-500 mt-2">Redaktionsschluss: {i.redaktionsschluss || "–"}</div>
            </div>
          ))}
          {data && data.recent_issues.length === 0 && (
            <div className="text-slate-400 text-sm">Noch keine Ausgaben. Lege deine erste Ausgabe an.</div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="issue-create-dialog">
          <DialogHeader><DialogTitle>Neue Ausgabe anlegen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name der Ausgabe *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="z.B. clubmagazin 1-2026" data-testid="issue-name-input" />
            </div>
            <div>
              <Label>Redaktionsschluss</Label>
              <Input value={form.redaktionsschluss} onChange={(e) => setForm({ ...form, redaktionsschluss: e.target.value })} type="date" className="mt-1" data-testid="issue-redaktionsschluss-input" />
            </div>
            <div>
              <Label>Kategorie-Vorlage</Label>
              <select value={form.category_template_id} onChange={(e) => setForm({ ...form, category_template_id: e.target.value })}
                className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="issue-category-template-select">
                <option value="">Standard (Editorial, Titelthema, Top)</option>
                {catTemplates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.categories.length} Kategorien)</option>)}
              </select>
            </div>
            <p className="text-xs text-slate-400">Die Kategorien werden aus der gewählten Vorlage angelegt und sind danach frei änderbar.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={createIssue} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="issue-create-button">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
